import parseLocale from '@/core/i18n/parse-locale';

/** Account landing on browse-only booking markets (no shop overview). */
export const BROWSE_ONLY_BOOKING_ACCOUNT_HOME = '/account/bookings';

/**
 * Browse-only countries where the self-service booking tool is open.
 * Shop, cart, and checkout stay closed. Dubai (AE) is the first of these.
 */
const BOOKING_TOOL_BROWSE_ONLY_COUNTRY_IDS = ['AE'] as const;

/**
 * Paths the booking flow needs on a browse-only locale.
 * `/experiences` is included so the locations list can offer Book now.
 */
const BOOKING_TOOL_BROWSE_ONLY_PATHS = [
  '/experiences',
  '/booking',
  '/bookings',
  '/account/bookings',
  '/account/lookup',
  '/account/signin',
  '/account/signup',
  '/account/reset-password',
  '/account/set-password',
] as const;

export function isBookingToolOpenOnBrowseOnlyCountry(
  countryCode: string | undefined,
): boolean {
  if (!countryCode) return false;
  const normalized = countryCode.toUpperCase();
  return BOOKING_TOOL_BROWSE_ONLY_COUNTRY_IDS.some(id => id === normalized);
}

export function isBookingToolOpenOnBrowseOnlyLocale(locale: string): boolean {
  const { country } = parseLocale(locale);
  return isBookingToolOpenOnBrowseOnlyCountry(country);
}

function normalizePath(pathWithoutLocale: string): string {
  const withSlash = pathWithoutLocale.startsWith('/')
    ? pathWithoutLocale
    : `/${pathWithoutLocale}`;
  if (withSlash.length > 1 && withSlash.endsWith('/')) {
    return withSlash.slice(0, -1);
  }
  return withSlash;
}

export function isSelfServiceBookingPath(pathWithoutLocale: string): boolean {
  const normalized = normalizePath(pathWithoutLocale);
  return BOOKING_TOOL_BROWSE_ONLY_PATHS.some(
    path => normalized === path || normalized.startsWith(`${path}/`),
  );
}

/** True when a browse-only locale may serve this path for the booking launch. */
export function isBrowseOnlyBookingPathAllowed(
  locale: string,
  pathWithoutLocale: string,
): boolean {
  return (
    isBookingToolOpenOnBrowseOnlyLocale(locale) &&
    isSelfServiceBookingPath(pathWithoutLocale)
  );
}

/** Default account route after sign-in when no safe `redirect` param is set. */
export function defaultPostAuthAccountPath(locale: string): string {
  return isBookingToolOpenOnBrowseOnlyLocale(locale)
    ? BROWSE_ONLY_BOOKING_ACCOUNT_HOME
    : '/account';
}

export const getPostAuthAccountPathForLocale = defaultPostAuthAccountPath;

/**
 * On browse-only booking locales, the account sidebar only exposes bookings
 * (plus logout and the runtime “book” link). Shop items stay hidden.
 */
export type BrowseOnlyBookingAccountMenuItem = {
  path: string;
  dynamicBookingLink?: boolean;
  isLogout?: boolean;
};

export function isAccountMenuItemAllowedForBrowseOnlyBookingMarket(
  item: BrowseOnlyBookingAccountMenuItem,
  isBookingToolEnabled: boolean,
): boolean {
  if (item.isLogout) {
    return true;
  }
  if (item.dynamicBookingLink && isBookingToolEnabled) {
    return true;
  }
  const path = item.path.trim();
  if (!path) {
    return false;
  }
  if (path === BROWSE_ONLY_BOOKING_ACCOUNT_HOME || path.startsWith('/bookings')) {
    return true;
  }
  return false;
}
